/**
 * Compare facts — Session 4A. Pure templates + arithmetic, client-side over
 * already-scanned canonical records. States facts, never verdicts: no
 * ranking, no recommendation, no "better".
 */
import { apiClient } from "../services/api";

export const COMPARE_ROWS = [
  ["energy_kcal", "Energy", "kcal"],
  ["protein_g", "Protein", "g"],
  ["carbohydrate_g", "Carbs", "g"],
  ["sugars_g", "Sugars", "g"],
  ["fat_g", "Fat", "g"],
  ["saturated_fat_g", "Saturates", "g"],
  ["sodium_mg", "Sodium", "mg"],
];

export const GOALS = [
  { key: "less_sugar", label: "Less sugar", field: "sugars_g", dir: "low", phrase: "Lower in sugars" },
  { key: "less_salt", label: "Less salt", field: "sodium_mg", dir: "low", phrase: "Lower in sodium" },
  { key: "more_protein", label: "More protein", field: "protein_g", dir: "high", phrase: "Higher in protein" },
  { key: "more_fibre", label: "More fibre", field: "fibre_g", dir: "high", phrase: "Higher in fibre" },
  { key: "fewer_calories", label: "Fewer calories", field: "energy_kcal", dir: "low", phrase: "Lower in calories" },
];

const FACT_WORDS = {
  energy_kcal: "calories", protein_g: "protein", carbohydrate_g: "carbs",
  sugars_g: "sugars", fat_g: "fat", saturated_fat_g: "saturates", sodium_mg: "sodium",
};

export function productName(c, i) {
  const p = c?.product || {};
  return p.name || p.brand || `Product ${"ABC"[i] || i + 1}`;
}

export function per100(c) {
  return c?.nutrition_per_100 || null;
}

/** Facts for >20% relative differences, per 100g/ml. Max 3, largest first. */
export function diffFacts(products) {
  const facts = [];
  for (const [field] of COMPARE_ROWS) {
    const vals = products
      .map((c, i) => ({ i, v: per100(c)?.[field] }))
      .filter((x) => x.v != null && x.v > 0);
    if (vals.length < 2) continue;
    const lo = vals.reduce((a, b) => (b.v < a.v ? b : a));
    const hi = vals.reduce((a, b) => (b.v > a.v ? b : a));
    const rel = (hi.v - lo.v) / hi.v;
    if (rel <= 0.2) continue;
    const pct = Math.floor(rel * 100 + 0.5);
    const suffix = products.length > 2 ? ` than ${productName(products[hi.i], hi.i)}` : "";
    facts.push({
      pct,
      text: `${productName(products[lo.i], lo.i)} has ${pct}% less ${FACT_WORDS[field]} per 100g${suffix}.`,
    });
  }
  return facts.sort((a, b) => b.pct - a.pct).slice(0, 3).map((f) => f.text);
}

/** Goal filter: a factual statement about the ONE nutrient the user asked
 * about. Not a verdict on the product. */
export function goalWinner(products, goal) {
  const vals = products
    .map((c, i) => ({ i, v: per100(c)?.[goal.field] }))
    .filter((x) => x.v != null);
  if (vals.length < 2) return null;
  const pick = goal.dir === "low"
    ? vals.reduce((a, b) => (b.v < a.v ? b : a))
    : vals.reduce((a, b) => (b.v > a.v ? b : a));
  const others = vals.filter((x) => x.i !== pick.i);
  if (others.some((o) => o.v === pick.v)) return null; // tie — no statement
  return {
    index: pick.i,
    line: `${goal.phrase} per 100g: ${productName(products[pick.i], pick.i)}`,
  };
}

/** Fire-and-forget UI instrumentation. No PII. */
export function fireScanEvent(event, props = {}) {
  try {
    apiClient.post("/nutrition/scan-event", { event, props }).catch(() => {});
  } catch { /* never block UI */ }
}
